import { Button, SelectOptionType, addToast } from '@octano/global-ui';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, FormGroup, Row } from 'reactstrap';

import { saveStudentFile } from '../../../../api/requests/tuitionProcess';
import ContactSection from '../../../../components/form/ContactSection';
import { useStepState } from '../../../../components/step/useStepState';
import {
  BackgroundSectionType,
  EducationalBackgroundType,
} from '../../../../types/studentFileSectionTypes';
import { useTuitionProcess } from '../../TuitionProcessContext';
import BackgroundSection from './BackgroundSection';
import EducationalBackgroundSection from './EducationalBackgroundSection';
import EmergencyContactSection from './EmergencyContactSection';
import HealthCareSection from './HealthCareSection';
import ParentalEducationSection from './ParentalEducationSection';

export type FieldsStudentFile = {
  sexID: SelectOptionType;
  maritalStatus: SelectOptionType;
  addressStreet: string;
  addressNumber: string;
  addressExtra: string;
  commune: SelectOptionType | null;
  region: SelectOptionType;
  phone: string;
  cellphone: string;
  email: string;
  addressStreetFamily: string;
  addressNumberFamily: string;
  addressExtraFamily: string;
  regionFamily: SelectOptionType;
  communeFamily: SelectOptionType | null;
  phoneFamily: string;
  cellphoneFamily: string;
  emailFamily: string;
  emergencyNames: string;
  mobilePhoneNumberEmergency: string;
  emailEmergency: string;
  fatherEducationalLevel: SelectOptionType;
  motherEducationalLevel: SelectOptionType;
  familyGroup: SelectOptionType;
  parentalSurvival: SelectOptionType;
  healthCareSystem: SelectOptionType;
  healthCareProvider: SelectOptionType;
  healthCareContributorID: string;
  healthInsurance: SelectOptionType;
  healthInsuranceContributorID: string;
  phoneNumberHealthCare: string;
  otherHealthCare: string;
  healthcarePreferredCenter: string;
  healthcareHistory: string;
};

type StudentFileFormProps = {
  backgroundData?: BackgroundSectionType;
  educationalBackground?: EducationalBackgroundType;
  defaultValues?: DefaultValues<FieldsStudentFile>;
};

const StudentFileForm = ({
  backgroundData,
  educationalBackground,
  defaultValues,
}: StudentFileFormProps) => {
  const { nextStep } = useStepState();
  const {
    prefix: translationPrefix,
    postulationDetailId,
    onCancelFirstStep,
  } = useTuitionProcess();
  const prefix = `${translationPrefix}.studentFile`;
  const { t } = useTranslation();
  const { onError } = useTuitionProcess();
  const methods = useForm<FieldsStudentFile>({
    mode: 'onSubmit',
    defaultValues: defaultValues,
  });

  const onSubmit = async (values: FieldsStudentFile) => {
    if (values.commune && values.communeFamily) {
      const { data, error } = await saveStudentFile(postulationDetailId, {
        background: {
          sexId: +values.sexID.value,
          maritalStatusId: +values.maritalStatus.value,
        },
        contact: {
          addressStreet: values.addressStreet,
          addressNumber: values.addressNumber,
          addressExtra: values.addressExtra,
          communeId: +values.commune.value,
          phone: values.phone,
          cellPhone: values.cellphone,
          email: values.email,
        },
        familyContact: {
          addressStreet: values.addressStreetFamily,
          addressNumber: values.addressNumberFamily,
          addressExtra: values.addressExtraFamily,
          communeId: +values.communeFamily.value,
          phone: values.phoneFamily,
          cellPhone: values.cellphoneFamily,
          email: values.emailFamily,
        },
        emergencyContact: {
          name: values.emergencyNames,
          cellPhone: values.mobilePhoneNumberEmergency,
          email: values.emailEmergency,
        },
        healthCare: {
          systemId: +values.healthCareSystem.value,
          providerId: values.healthCareProvider?.value as number,
          contributorRut: values.healthCareContributorID,
          hasInsurance: Boolean(values.healthInsurance.value),
          insuranceContributorRut: values.healthInsuranceContributorID ?? null,
          insuranceCellPhone: values.phoneNumberHealthCare,
        },
        parentalEducationalBackground: {
          educationalLevelPaternalId: +values.fatherEducationalLevel.value,
          educationalLevelMaternalId: +values.motherEducationalLevel.value,
          familyGroupId: +values.familyGroup.value,
          parentalSurvivalId: +values.parentalSurvival.value,
        },
      });

      if (data === '') {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`${prefix}.saveSuccess`),
        });
        nextStep();
      } else if (error) {
        onError(error.code, () => {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.saveError`),
          });
        });
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        {/* Secciones del formulario */}
        <BackgroundSection data={backgroundData} />
        <EducationalBackgroundSection
          educationalBackground={educationalBackground}
        />
        <ContactSection prefix={`${prefix}.academicContactSection`} />
        <ContactSection
          prefix={`${prefix}.familyGroupSection`}
          fieldNames={{
            addressStreet: 'addressStreetFamily',
            addressNumber: 'addressNumberFamily',
            addressExtra: 'addressExtraFamily',
            region: 'regionFamily',
            commune: 'communeFamily',
            city: 'cityFamily',
            phone: 'phoneFamily',
            cellphone: 'cellphoneFamily',
            email: 'emailFamily',
          }}
        />
        <EmergencyContactSection />
        <ParentalEducationSection />
        <HealthCareSection />

        <Row className="pt-5 justify-content-end">
          {onCancelFirstStep && (
            <Col xs={12} lg={4} className="order-2 order-lg-1">
              <FormGroup>
                <Button
                  outlined
                  text={t(`common.actions.cancel`)}
                  fullwidth
                  onClick={onCancelFirstStep}
                />
              </FormGroup>
            </Col>
          )}
          <Col xs={12} lg={4} className="order-1 order-lg-2">
            <FormGroup>
              <Button
                type="submit"
                text={t(`common.actions.next`)}
                loading={methods.formState.isSubmitting}
                fullwidth
              />
            </FormGroup>
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};

export default StudentFileForm;
